// src/components/settings/targets/ResultMappingSection.tsx
/**
 * นิยาม Result (DEC3 option B, spec §3): which metric each objective's Result
 * counts in this workspace. Only Awareness has a choice (Impressions or Reach
 * รวมรายวัน). Admin and master change it at once; analyst and viewer read it.
 * Every change is a version (history below) and an audit entry
 * (set_result_mapping). A failed save keeps the selection; a failed read never
 * shows a table under a guessed definition.
 */
import { dataDateOf } from '@/services/dataDate';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AWARENESS_METRICS, isAwarenessMetric, mappingHistory, metricFor, type AwarenessMetric } from '@/services/targets/resultMetric';
import { otherMetricTargetCount, TARGET_OBJECTIVES, type CprTargetRow } from '@/services/targets/targetRules';
import { setResultMapping } from '@/services/targets/targetsService';
import { announceTargetsChanged } from '@/services/targets/targetsEvents';
import type { ResultMappingsState } from '@/hooks/useResultMappings';
import { COST_LABEL, failureOf, OBJECTIVE_LABEL, RESULT_LABEL, whoLabel, type TargetRole, type WriteFailure } from './targetUi';

interface Props {
  workspaceId: string;
  role: TargetRole;
  me: string | null;
  mapping: ResultMappingsState;
  /** Every target row; the warning counts the approved Awareness ones set against another metric (R9). */
  targets: readonly CprTargetRow[];
  /** After a successful save: reload the mapping and the targets. */
  onSaved: () => void;
  reloadMapping: () => void;
}

const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-6">
      <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('targets.mapping.heading')}</h2>
      {children}
    </section>
  );
};

export const ResultMappingSection: React.FC<Props> = ({ workspaceId, role, me, mapping, targets, onSaved, reloadMapping }) => {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<AwarenessMetric | null>(null);
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [busy, setBusy] = useState(false);
  // Saved but the reload has not landed: keep showing the choice, hide Save/Cancel (final-review m2).
  const [saved, setSaved] = useState(false);

  // Release the choice once the mapping reads back the value the admin chose.
  useEffect(() => {
    if (choice !== null && mapping.status === 'ready' && mapping.awareness === choice) {
      setChoice(null);
      setSaved(false);
    }
  }, [mapping, choice]);

  if (mapping.status === 'loading') return <Frame><p className="text-sm text-neutral-700">{t('common.loading')}</p></Frame>;
  if (mapping.status === 'failed') {
    return (
      <Frame>
        <p role="alert" className="text-sm text-error-700">{t('targets.mapping.loadFailed')}</p>
        <button type="button" className="mt-2 text-xs font-medium underline" onClick={reloadMapping}>{t('targets.reload')}</button>
      </Frame>
    );
  }

  const canEdit = role === 'admin' || role === 'master';
  const current = mapping.awareness;
  const selected = choice ?? current;
  const changed = selected !== current && !saved;
  const orphaned = changed ? otherMetricTargetCount(targets, 'awareness', selected) : 0;
  const history = mappingHistory(mapping.rows);

  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await setResultMapping({ workspaceId, objective: 'awareness', metric: selected });
      setSaved(true);
      announceTargetsChanged();
      onSaved();
    } catch (err) {
      setFailure(failureOf(t, err)); // the selection stays as chosen
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="text-xs text-neutral-700">
              <th className="px-3 py-2">{t('targets.cpr.objective')}</th>
              <th className="px-3 py-2">{t('targets.mapping.result')}</th>
              <th className="px-3 py-2">{t('targets.mapping.cost')}</th>
              <th className="px-3 py-2">{t('targets.mapping.control')}</th>
            </tr>
          </thead>
          <tbody>
            {TARGET_OBJECTIVES.map((o) => {
              const metric = metricFor(o, selected);
              return (
                <tr key={o} data-testid={`mapping-${o}`} className="border-t border-neutral-100">
                  <th scope="row" className="px-3 py-2 font-medium text-ink-900 whitespace-nowrap">{OBJECTIVE_LABEL[o](t)}</th>
                  <td className="px-3 py-2 text-ink-900">{RESULT_LABEL[metric](t)}</td>
                  <td className="px-3 py-2 text-ink-900">{COST_LABEL[metric](t)}</td>
                  <td className="px-3 py-2 text-neutral-700">
                    {o !== 'awareness' ? t('targets.mapping.central') : canEdit ? (
                      <select aria-label={t('targets.mapping.selectAria')} className="rounded border border-neutral-300 px-2 py-1 text-ink-900"
                        value={selected} disabled={busy}
                        onChange={(e) => { if (isAwarenessMetric(e.target.value)) { setChoice(e.target.value); setSaved(false); setFailure(null); } }}>
                        {AWARENESS_METRICS.map((m) => <option key={m} value={m}>{RESULT_LABEL[m](t)}</option>)}
                      </select>
                    ) : RESULT_LABEL[current](t)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selected === 'reach' && <p className="mt-3 text-xs text-warning-700">{t('targets.mapping.caveat')}</p>}
      {changed && orphaned > 0 && (
        <p className="mt-2 text-xs text-ink-900">
          {t('targets.mapping.orphaned', { from: RESULT_LABEL[current](t), to: RESULT_LABEL[selected](t), n: orphaned })}
        </p>
      )}
      {canEdit && changed && (
        <div className="mt-3 flex gap-2">
          <button type="button" className="rounded bg-ink-900 px-3 py-1 text-xs font-medium text-white disabled:opacity-60" disabled={busy} onClick={save}>{t('targets.save')}</button>
          <button type="button" className="rounded border border-neutral-300 px-3 py-1 text-xs font-medium" disabled={busy}
            onClick={() => { setChoice(null); setSaved(false); setFailure(null); }}>{t('targets.cancel')}</button>
        </div>
      )}
      {failure && (
        <p role="alert" className="mt-2 text-xs text-error-700">
          {failure.message}
          {failure.offerReload && <button type="button" className="ml-2 underline" onClick={reloadMapping}>{t('targets.reload')}</button>}
        </p>
      )}
      <h3 className="mt-4 mb-1 text-xs font-semibold text-ink-900">{t('targets.mapping.historyHeading')}</h3>
      {history.length === 0 ? (
        <p className="text-xs text-neutral-700">{t('targets.mapping.historyEmpty')}</p>
      ) : (
        <ul className="text-xs text-ink-900">
          {history.map((h) => (
            <li key={h.id} className="py-1 border-t border-neutral-100">
              {`${dataDateOf(h.setAt)} · ${whoLabel(t, h.setBy, me)} · ${t('targets.mapping.historyEntry', { from: RESULT_LABEL[h.from](t), to: RESULT_LABEL[h.to](t) })}`}
            </li>
          ))}
        </ul>
      )}
    </Frame>
  );
};
